import type { Ref } from 'vue'
import type { AssessmentTemplateListRow } from './interfaces'

import { useAssessmentTemplatesStore } from '../assessment-templates/store'
import { useAssessmentTemplateDuplicateApi } from '../../api/assessment-templates/duplicate.hook'
import { getAssessmentEditPath } from './assessment-template-options'

interface ModalDuplicateAssessmentTemplateReturn {
  refIsOpen: ComputedRef<boolean>
  refIsLoading: Ref<boolean>
  refTemplate: ComputedRef<AssessmentTemplateListRow | null>
  closeModal: () => void
  confirmDuplicate: () => Promise<void>
}

/**
 * Duplicate — the safe way to revise a template that assessments already reference. The copy is an
 * independent row, so editing it can never reach an in-flight attempt on the original. The new
 * title is composed server-side (original + " (copy)", §6), so nothing here builds it.
 */
export const useModalDuplicateAssessmentTemplate = (): ModalDuplicateAssessmentTemplateReturn => {
  const router = useRouter()
  const store = useAssessmentTemplatesStore()

  const refIsLoading = ref(false)

  const refIsOpen = computed<boolean>(() => store.isModalDuplicate)
  const refTemplate = computed<AssessmentTemplateListRow | null>(() => store.getTemplateToDuplicate)

  const closeModal = (): void => {
    store.setTemplateToDuplicate(null)
  }

  /**
   * On success the response's `template.id` is the NEW row, and the editor is opened on it rather
   * than the list refetched: the only reason to duplicate is to edit the copy.
   */
  const confirmDuplicate = async (): Promise<void> => {
    const template = refTemplate.value
    if (!template || refIsLoading.value) return

    await useAssessmentTemplateDuplicateApi({
      templateId: template.id,
      onStart: () => { refIsLoading.value = true },
      onSuccess: (created) => {
        closeModal()
        void router.push(getAssessmentEditPath(created.id))
      },
      onFinish: () => { refIsLoading.value = false },
    })
  }

  return {
    refIsOpen,
    refIsLoading,
    refTemplate,
    closeModal,
    confirmDuplicate,
  }
}
